import { skipToken } from "@reduxjs/toolkit/query";
import { Color } from "@repo/app-types/constants/colors";
import {
  GENESYS_FAILED_IMPORTS_BULK_MAX_IDS,
  getGenesysFailedImportErrorLabel,
  type GenesysFailedImportRow,
  type GenesysRecordingId,
} from "@repo/app-types/entities/genesysRecording";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Checkbox } from "@repo/ui/Checkbox/checkbox";
import { useCopyAction } from "@repo/ui/clipboard/useCopyAction";
import { Size } from "@repo/ui/helpers/helpers";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { Pill } from "@repo/ui/Pills/pill";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { Dialog, type SortDescriptor } from "react-aria-components";
import { Navigate } from "react-router";
import {
  useGenesysFailedImportsInfiniteQuery,
  useReprocessGenesysFailedImportsMutation,
  useSkipGenesysFailedImportsMutation,
} from "../../../api/entities/genesysIntegration.js";
import { appPaths } from "../../../app/appPaths.js";
import { useTeammateAuthorization } from "../../../helpers/teammateAuthorizationContext.js";
import { useGenesysDetailOutletContext } from "./genesysIntegrationDetailSection.js";

/** A row action, and the rows it targets — one row, or the bulk selection. */
type FailedImportAction = "reprocess" | "skip";
interface PendingAction {
  action: FailedImportAction;
  ids: readonly GenesysRecordingId[];
}

/** A small copy-to-clipboard control shown beside a truncated id. */
const CopyButton: FC<{ text: string; label: string }> = ({ text, label }) => {
  const { didJustCopy, onCopy } = useCopyAction(text);
  return (
    <button
      type="button"
      onClick={onCopy}
      aria-label={`Copy ${label}`}
      className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
    >
      {didJustCopy ? (
        <IconCheck className="size-3.5 text-success" />
      ) : (
        <IconCopy className="size-3.5" />
      )}
    </button>
  );
};

// The row's `id` (the ledger UUID) is not shown — it's meaningless to a
// customer — but it stays the row identity used for selection and the
// reprocess/skip payloads.
const baseColumns: readonly EntityTableColumnDefinition<GenesysFailedImportRow>[] =
  [
    {
      id: "recorded_at",
      header: "Recorded at",
      isRowHeader: true,
      allowsSorting: true,
      getTextValue: (row) => row.recordedAt,
      renderCell: (row) => (
        <span className="whitespace-nowrap text-foreground">
          {DateTime.fromISO(row.recordedAt).toLocaleString(
            DateTime.DATETIME_MED,
          )}
        </span>
      ),
    },
    {
      id: "conversation",
      header: "Genesys conversation ID",
      getTextValue: (row) => row.genesysConversationId,
      // Plain copyable text for now — the Genesys admin-console deep-link format
      // is unverified, so we show the id and will re-introduce the link once it
      // is confirmed against a real org. `row.region` stays plumbed for that.
      renderCell: (row) => (
        <div className="flex items-center gap-1.5">
          <span
            className="block max-w-[9rem] truncate font-mono text-xs text-muted-foreground"
            title={row.genesysConversationId}
          >
            {row.genesysConversationId}
          </span>
          <CopyButton
            text={row.genesysConversationId}
            label="conversation ID"
          />
        </div>
      ),
    },
    {
      id: "recording",
      header: "Genesys recording ID",
      getTextValue: (row) => row.genesysRecordingId,
      renderCell: (row) => (
        <div className="flex items-center gap-1.5">
          <span
            className="block max-w-[9rem] truncate font-mono text-xs text-muted-foreground"
            title={row.genesysRecordingId}
          >
            {row.genesysRecordingId}
          </span>
          <CopyButton text={row.genesysRecordingId} label="recording ID" />
        </div>
      ),
    },
    {
      id: "error",
      header: "Error",
      getTextValue: (row) => row.errorCode ?? "",
      renderCell: (row) => {
        const label = getGenesysFailedImportErrorLabel(row.errorCode);
        if (!label) {
          return <span className="text-muted-foreground">—</span>;
        }
        // The friendly label on the pill; the raw message in the tooltip.
        return (
          <span title={row.errorMessage ?? undefined}>
            <Pill label={label} size={Size.SMALL} color={Color.Red} />
          </span>
        );
      },
    },
  ];

/**
 * The leading selection column: a per-row checkbox plus a "select all" header
 * that reflects/toggles the currently-loaded rows (indeterminate when only some
 * are selected). Only rendered for teammates who can act on the rows.
 */
function selectionColumn(
  rows: readonly GenesysFailedImportRow[],
  selectedIds: ReadonlySet<GenesysRecordingId>,
  onToggleRow: (id: GenesysRecordingId, shouldSelect: boolean) => void,
  onToggleAll: (shouldSelect: boolean) => void,
): EntityTableColumnDefinition<GenesysFailedImportRow> {
  const allSelected =
    rows.length > 0 && rows.every((row) => selectedIds.has(row.id));
  const someSelected = rows.some((row) => selectedIds.has(row.id));
  return {
    id: "select",
    className: "w-10",
    header: (
      <Checkbox
        isChecked={allSelected}
        isIndeterminate={!allSelected && someSelected}
        onChange={onToggleAll}
        accessibleLabel="Select all loaded rows"
      />
    ),
    renderCell: (row) => (
      <Checkbox
        isChecked={selectedIds.has(row.id)}
        onChange={(shouldSelect) => onToggleRow(row.id, shouldSelect)}
        accessibleLabel={`Select ${row.genesysConversationId}`}
      />
    ),
  };
}

/** The trailing per-row actions: Reprocess and Skip, gated behind a confirm. */
function actionsColumn(
  onAction: (action: FailedImportAction, id: GenesysRecordingId) => void,
): EntityTableColumnDefinition<GenesysFailedImportRow> {
  return {
    id: "actions",
    header: "Actions",
    renderCell: (row) => (
      <div className="flex justify-end gap-2 whitespace-nowrap">
        <Button
          size={Size.SMALL}
          variant={ButtonVariant.Secondary}
          onClick={() => onAction("reprocess", row.id)}
        >
          Reprocess
        </Button>
        <Button
          size={Size.SMALL}
          variant={ButtonVariant.Secondary}
          onClick={() => onAction("skip", row.id)}
        >
          Skip
        </Button>
      </div>
    ),
  };
}

/** The bulk action bar shown when ≥1 row is selected. */
const BulkActionBar: FC<{
  count: number;
  isBusy: boolean;
  onReprocess: () => void;
  onSkip: () => void;
  onClear: () => void;
}> = ({ count, isBusy, onReprocess, onSkip, onClear }) => (
  <div className="flex flex-wrap items-center gap-3 rounded-md border border-border-light bg-muted px-3 py-2">
    <span className="text-sm font-medium text-foreground">
      {count.toLocaleString()} selected
    </span>
    <div className="flex items-center gap-2">
      <Button
        size={Size.SMALL}
        variant={ButtonVariant.Secondary}
        isDisabled={isBusy}
        onClick={onReprocess}
      >
        Reprocess
      </Button>
      <Button
        size={Size.SMALL}
        variant={ButtonVariant.Secondary}
        isDisabled={isBusy}
        onClick={onSkip}
      >
        Skip
      </Button>
      <Button
        size={Size.SMALL}
        variant={ButtonVariant.Tertiary}
        onClick={onClear}
      >
        Clear
      </Button>
    </div>
  </div>
);

/**
 * The Genesys detail page's **Failed imports** tab: the caller company's own
 * `failed` `genesys_recording` rows, with infinite scroll and — for teammates
 * who can update integrations — the ability to reprocess (re-import) or skip
 * them, per-row or in bulk over the loaded rows. The customer twin of the HQ
 * `/recording-genesys` triage page, scoped to one company and to `failed`.
 */
export const GenesysFailedImportsTab: FC = () => {
  const { integration, isLoading: isIntegrationLoading } =
    useGenesysDetailOutletContext();
  const ability = useTeammateAuthorization();
  const canUpdate = ability.can(
    PermissionActions.UPDATE,
    PermissionSubjects.INTEGRATIONS,
  );

  // Only the "Recorded at" column sorts; clicking its header flips the direction.
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "recorded_at",
    direction: "descending",
  });
  const order = sortDescriptor.direction === "ascending" ? "asc" : "desc";

  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGenesysFailedImportsInfiniteQuery(integration ? { order } : skipToken);

  const [reprocess, { isLoading: isReprocessing }] =
    useReprocessGenesysFailedImportsMutation();
  const [skip, { isLoading: isSkipping }] =
    useSkipGenesysFailedImportsMutation();
  const isBusy = isReprocessing || isSkipping;

  const [selectedIds, setSelectedIds] = useState<
    ReadonlySet<GenesysRecordingId>
  >(new Set());
  const [pending, setPending] = useState<PendingAction | null>(null);

  const rows = useMemo(
    () => data?.pages.flatMap((entry) => entry.rows) ?? [],
    [data],
  );

  // "Select all" only covers the rows one bulk action accepts.
  const selectableRows = useMemo(
    () => rows.slice(0, GENESYS_FAILED_IMPORTS_BULK_MAX_IDS),
    [rows],
  );

  // Prune selection to the rows currently shown, so ids dropped by a refetch —
  // e.g. after a reprocess/skip moves them off this view — don't linger.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) {
        return prev;
      }
      const visible = new Set(rows.map((row) => row.id));
      const next = new Set<GenesysRecordingId>();
      for (const id of prev) {
        if (visible.has(id)) {
          next.add(id);
        }
      }
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const toggleRow = useCallback(
    (id: GenesysRecordingId, shouldSelect: boolean) => {
      setSelectedIds((prev) => {
        // Never let the selection grow past what one bulk action accepts — the
        // header "select all" already stops at the cap, and this bounds
        // hand-selection too, so the payload can never 400 against the server.
        if (
          shouldSelect &&
          !prev.has(id) &&
          prev.size >= GENESYS_FAILED_IMPORTS_BULK_MAX_IDS
        ) {
          return prev;
        }
        const next = new Set(prev);
        if (shouldSelect) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });
    },
    [],
  );

  const toggleAll = useCallback(
    (shouldSelect: boolean) => {
      setSelectedIds(
        shouldSelect ? new Set(selectableRows.map((row) => row.id)) : new Set(),
      );
    },
    [selectableRows],
  );

  const requestRowAction = useCallback(
    (action: FailedImportAction, id: GenesysRecordingId) =>
      setPending({ action, ids: [id] }),
    [],
  );

  const columns = useMemo(
    () =>
      canUpdate
        ? [
            selectionColumn(selectableRows, selectedIds, toggleRow, toggleAll),
            ...baseColumns,
            actionsColumn(requestRowAction),
          ]
        : baseColumns,
    [
      canUpdate,
      selectableRows,
      selectedIds,
      toggleRow,
      toggleAll,
      requestRowAction,
    ],
  );

  const selectedIdList = useMemo(() => [...selectedIds], [selectedIds]);
  const selectedCount = selectedIdList.length;

  const runPending = useCallback(async () => {
    if (!pending) {
      return;
    }
    const { action, ids } = pending;
    setPending(null);
    try {
      if (action === "reprocess") {
        await reprocess(ids).unwrap();
      } else {
        await skip(ids).unwrap();
      }
      clearSelection();
    } catch {
      // A thrown error surfaces via the mutation's error state; nothing to do.
    }
  }, [pending, reprocess, skip, clearSelection]);

  // Wait while the shared integration is still loading — on a direct visit or a
  // refresh it hasn't resolved yet, and redirecting on that transient `null`
  // would bounce every deep link to this tab back to Settings.
  if (!integration) {
    if (isIntegrationLoading) {
      return <Loader style={LoaderStyle.SPINNER} />;
    }
    // Genuinely disconnected: there are no failed imports to show, and the tab
    // strip is hidden, so send the visitor to the connection form.
    return (
      <Navigate
        to={appPaths.settingsIntegration.build({ integrationSlug: "genesys" })}
        replace
      />
    );
  }

  const pendingCount = pending?.ids.length ?? 0;
  const isSkip = pending?.action === "skip";

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
        Recordings that couldn&apos;t be imported from Genesys Cloud. Reprocess
        one to try importing it again, or skip it to dismiss it.
      </p>

      {canUpdate && selectedCount > 0 ? (
        <BulkActionBar
          count={selectedCount}
          isBusy={isBusy}
          onReprocess={() =>
            setPending({ action: "reprocess", ids: selectedIdList })
          }
          onSkip={() => setPending({ action: "skip", ids: selectedIdList })}
          onClear={clearSelection}
        />
      ) : null}

      {/*
        EntityTable has no scroll container of its own; a bounded, scrollable box
        gives its sticky header a range to stick against and keeps a wide table
        from scrolling the whole page.
      */}
      <div className="max-h-[70vh] overflow-auto rounded-lg border border-border-light">
        <EntityTable
          aria-label="Failed Genesys imports"
          columns={columns}
          entities={rows}
          isLoading={isLoading}
          isError={isError}
          sortDescriptor={sortDescriptor}
          onSortChange={setSortDescriptor}
          onLoadMore={
            hasNextPage && !isFetchingNextPage
              ? () => void fetchNextPage()
              : undefined
          }
          emptyMessage="No failed imports"
        />
      </div>

      <CenteredModal
        isOpen={pending !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPending(null);
          }
        }}
        isDismissable
      >
        <Dialog className="w-screen max-w-md space-y-4 p-4">
          <ModalHeader isCloseVisible>
            {isSkip ? "Skip failed imports" : "Reprocess failed imports"}
          </ModalHeader>
          <p className="text-sm text-muted-foreground">
            {isSkip
              ? `Dismiss ${pendingCount.toLocaleString()} failed import${pendingCount === 1 ? "" : "s"}. They'll be removed from this list.`
              : `Try importing ${pendingCount.toLocaleString()} recording${pendingCount === 1 ? "" : "s"} again from Genesys Cloud.`}
          </p>
          <div className="flex justify-end gap-2">
            <Button
              size={Size.MEDIUM}
              variant={ButtonVariant.Secondary}
              slot="close"
            >
              Cancel
            </Button>
            <Button
              size={Size.MEDIUM}
              variant={ButtonVariant.Primary}
              onClick={() => void runPending()}
            >
              {isSkip ? "Skip" : "Reprocess"}
            </Button>
          </div>
        </Dialog>
      </CenteredModal>
    </div>
  );
};
